import { ComponentType, lazy, LazyExoticComponent } from 'react';

const RELOADED_KEY = 'lazy-page-reloaded';

/**
 * A page's code downloads the first time it is opened rather than with the
 * whole app. After a release the old files are gone, so a tab opened before it
 * can fail to fetch a page it hasn't loaded yet: it reloads once to pick up the
 * new version instead of showing a blank screen.
 */
const load = <T>(importer: () => Promise<T>): Promise<T> =>
  importer().then(
    (module) => {
      try {
        sessionStorage.removeItem(RELOADED_KEY);
      } catch {
        // Nothing to forget.
      }
      return module;
    },
    (error) => {
      let reloaded = true;
      try {
        reloaded = sessionStorage.getItem(RELOADED_KEY) === '1';
        if (!reloaded) {
          sessionStorage.setItem(RELOADED_KEY, '1');
        }
      } catch {
        // Without session storage, don't risk reloading in a loop.
      }
      if (!reloaded) {
        window.location.reload();
        return new Promise<T>(() => undefined);
      }
      throw error;
    },
  );

/** React.lazy for a module whose page is its default export. */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const lazyPage = <P = any>(importer: () => Promise<{ default: ComponentType<P> }>): LazyExoticComponent<ComponentType<P>> =>
  lazy(() => load(importer));

/** React.lazy for a page exported by name. */
export const lazyNamed = <M, K extends keyof M>(importer: () => Promise<M>, name: K): LazyExoticComponent<M[K] & ComponentType<any>> => // eslint-disable-line @typescript-eslint/no-explicit-any
  lazy(() => load(importer).then((module) => ({ default: module[name] as M[K] & ComponentType<any> }))); // eslint-disable-line @typescript-eslint/no-explicit-any
